'use client';

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

export function trackLeadGeneration(source: string, puppyId?: string) {
  trackEvent('generate_lead', {
    source,
    puppy_id: puppyId,
    page: window.location.pathname,
    event_category: 'Lead Generation',
    value: 50
  });
}

export function trackPuppyInquiry(puppyId: string, puppyName: string) {
  trackEvent('puppy_inquiry', {
    puppy_id: puppyId,
    puppy_name: puppyName,
    page: window.location.pathname,
    event_category: 'Product Interest',
    value: 30
  });
}

export function trackContactSubmission(formType: string) {
  trackEvent('contact_form_submit', {
    form_type: formType,
    page: window.location.pathname,
    event_category: 'Lead Generation',
    value: 40
  });
}

export function trackNewsletterSignup(source: string) {
  trackEvent('newsletter_signup', {
    source,
    page: window.location.pathname,
    event_category: 'Engagement',
    value: 10
  });
}

// Enhanced conversion tracking for Google Ads
export function trackConversion(conversionLabel: string, value: number = 0) {
  if (typeof window !== 'undefined' && window.gtag) {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    window.gtag('event', 'conversion', {
      send_to: `${measurementId}/${conversionLabel}`,
      value: value,
      currency: 'USD'
    });
  }
}

// Web Vitals and Performance Monitoring

export interface WebVitalsMetric {
  id: string;
  name: string;
  value: number;
  delta: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

// Track Core Web Vitals
export function reportWebVitals(metric: WebVitalsMetric) {
  const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  
  if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_rating: metric.rating,
      metric_delta: metric.delta,
      non_interaction: true,
    });
  }

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vitals] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
    });
  }
}

// Performance monitoring utilities
export function measurePerformance(label: string, callback: () => void) {
  if (typeof window === 'undefined') return;

  const startTime = performance.now();
  callback();
  const endTime = performance.now();
  const duration = endTime - startTime;

  if (process.env.NODE_ENV === 'development') {
    console.log(`[Performance] ${label}: ${duration.toFixed(2)}ms`);
  }

  // Track in GA if available
  if (window.gtag && process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID) {
    window.gtag('event', 'timing_complete', {
      name: label,
      value: Math.round(duration),
      event_category: 'Performance',
    });
  }
}

// Resource timing monitoring
export function monitorResourceTiming() {
  if (typeof window === 'undefined' || !window.performance) return;

  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const slowResources = resources.filter(resource => resource.duration > 1000);

  if (slowResources.length > 0 && process.env.NODE_ENV === 'development') {
    console.warn('[Performance] Slow resources detected:', slowResources.map(r => ({
      name: r.name,
      duration: `${r.duration.toFixed(2)}ms`,
      type: r.initiatorType,
    })));
  }
}

// Layout shift monitoring
export function monitorLayoutShifts() {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
          const value = (entry as any).value;
          if (value > 0.1 && process.env.NODE_ENV === 'development') {
            console.warn('[CLS] Layout shift detected:', {
              value,
              sources: (entry as any).sources,
            });
          }
        }
      }
    });

    observer.observe({ entryTypes: ['layout-shift'] });
  } catch (e) {
    // PerformanceObserver not supported
  }
}